/**
 * Request Deduplication Utility
 * Prevents duplicate API calls for the same request
 * 
 * Usage:
 * const result = await deduplicateRequest('key', async () => {
 *   return await someAPICall();
 * });
 */

const pendingRequests = new Map();
const requestCache = new Map();

// Cache TTL: 1 minute for API responses
const CACHE_TTL = 60000;

/**
 * Deduplicate a request - if same request is in progress, return the existing promise
 * @param {string} key - Unique key for the request
 * @param {Function} requestFn - Function that performs the actual request
 * @param {Object} options - Options
 * @param {boolean} options.useCache - Whether to use cache (default: true)
 * @param {number} options.cacheTTL - Cache TTL in ms (default: 60000)
 * @returns {Promise} Request result
 */
export const deduplicateRequest = async (key, requestFn, options = {}) => {
  const { useCache = true, cacheTTL = CACHE_TTL } = options;
  
  // Check cache first
  if (useCache) {
    const cached = requestCache.get(key);
    if (cached && Date.now() - cached.timestamp < cacheTTL) {
      console.log('🔄 Cache hit for request:', key);
      return cached.data;
    }
  }
  
  // Check if request is already in progress
  if (pendingRequests.has(key)) {
    console.log('🔄 Deduplicating request (already in progress):', key);
    return pendingRequests.get(key);
  }
  
  // Create new request promise
  const promise = requestFn()
    .then(result => {
      // Cache successful results
      if (useCache) {
        requestCache.set(key, {
          data: result,
          timestamp: Date.now()
        });
      }
      return result;
    })
    .finally(() => {
      // Clean up pending request
      pendingRequests.delete(key);
    });
  
  // Store pending request
  pendingRequests.set(key, promise);
  
  return promise;
};

/**
 * Clear request cache
 * @param {string} key - Optional: clear specific key, or all if not provided
 */
export const clearRequestCache = (key = null) => {
  if (key) {
    requestCache.delete(key);
    pendingRequests.delete(key);
  } else {
    requestCache.clear();
    pendingRequests.clear();
  }
};

/**
 * Get cache statistics
 */
export const getCacheStats = () => {
  return {
    pendingRequests: pendingRequests.size,
    cachedRequests: requestCache.size,
    cacheKeys: Array.from(requestCache.keys())
  };
};

